import React, { useState } from "react";
import { post } from "@/lib/serverComm";

interface QueryResponse {
  success: boolean;
  data: {
    columns: string[];
    rows: unknown[][];
    numRows: number;
    numColumns: number;
    [key: string]: unknown;
  };
}

const QueryPage: React.FC = () => {
  const [userId, setUserId] = useState("");
  const [datasetType, setDatasetType] = useState("main");
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"table" | "json">("table");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId.trim()) {
      setError("Please enter a user ID");
      return;
    }

    if (!datasetType.trim()) {
      setError("Please select a dataset type");
      return;
    }

    if (!query.trim()) {
      setError("Please enter a query");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await post("/local/query", {
        userId,
        datasetType,
        query,
      });
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Database Query Utility</h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <label
              htmlFor="userId"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              User ID
            </label>
            <input
              type="text"
              id="userId"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="Enter user ID..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            />
          </div>

          <div className="flex-1">
            <label
              htmlFor="datasetType"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Dataset Type
            </label>
            <select
              id="datasetType"
              value={datasetType}
              onChange={(e) => setDatasetType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            >
              <option value="main">Main</option>
              <option value="benchmark">Benchmark</option>
              <option value="attrition_drivers">Attrition Drivers</option>
              <option value="attrition_directional_drivers">
                Attrition Directional Drivers
              </option>
              <option value="recruitment_applications">Recruitment</option>
              <option value="time_to_fill_drivers">Time to Fill Drivers</option>
              <option value="faq">FAQ</option>
              <option value="culture">Culture</option>
              <option value="employee_relations">Employee Relations</option>
              <option value="osha_incidents">OSHA Incidents</option>
              <option value="employee_goals">Employee Goals</option>
              <option value="lightcast">Lightcast</option>
            </select>
          </div>
        </div>

        <div>
          <label
            htmlFor="query"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            SQL Query
          </label>
          <textarea
            id="query"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter your SQL query here..."
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          />
        </div>

        <button
          type="submit"
          disabled={
            loading || !userId.trim() || !datasetType.trim() || !query.trim()
          }
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Executing..." : "Execute Query"}
        </button>
      </form>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {result !== null && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-3">Query Result</h2>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("table")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "table"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Table View
              </button>
              <button
                onClick={() => setActiveTab("json")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "json"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Raw JSON
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === "table" ? (
            (() => {
              // Type guard function to check if result has the expected structure
              const isValidTableData = (obj: unknown): obj is QueryResponse => {
                if (
                  typeof obj !== "object" ||
                  obj === null ||
                  !("data" in obj)
                ) {
                  return false;
                }
                const data = (obj as { data: unknown }).data;
                if (typeof data !== "object" || data === null) {
                  return false;
                }
                const dataObj = data as Record<string, unknown>;
                return (
                  "columns" in dataObj &&
                  "rows" in dataObj &&
                  Array.isArray(dataObj.columns) &&
                  Array.isArray(dataObj.rows) &&
                  dataObj.columns.every(
                    (col: unknown) => typeof col === "string",
                  )
                );
              };

              if (isValidTableData(result)) {
                return (
                  <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {result.data.columns.map((column, index) => (
                              <th
                                key={index}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider"
                              >
                                {column}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {(result as QueryResponse).data.rows.map(
                            (row, rowIndex) => (
                              <tr
                                key={rowIndex}
                                className={
                                  rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                                }
                              >
                                {row.map((cell, cellIndex) => (
                                  <td
                                    key={cellIndex}
                                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                  >
                                    {cell === null || cell === undefined ? (
                                      <span className="text-gray-400 italic">
                                        null
                                      </span>
                                    ) : typeof cell === "number" ? (
                                      cell < 1 && cell > 0 && cell !== 0 ? (
                                        cell.toFixed(6)
                                      ) : (
                                        cell.toLocaleString()
                                      )
                                    ) : typeof cell === "boolean" ? (
                                      cell ? (
                                        "true"
                                      ) : (
                                        "false"
                                      )
                                    ) : (
                                      String(cell)
                                    )}
                                  </td>
                                ))}
                                {/* Fill empty cells if row has fewer columns than header */}
                                {row.length <
                                  (result as QueryResponse).data.columns
                                    .length &&
                                  Array.from({
                                    length:
                                      (result as QueryResponse).data.columns
                                        .length - row.length,
                                  }).map((_, index) => (
                                    <td
                                      key={`empty-${index}`}
                                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 italic"
                                    >
                                      empty
                                    </td>
                                  ))}
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
                      {result.data.numRows || result.data.rows.length} rows ×{" "}
                      {result.data.numColumns || result.data.columns.length}{" "}
                      columns
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <p className="text-yellow-800">
                      This response doesn't appear to be tabular data. Switch to
                      the "Raw JSON" tab to view the full response.
                    </p>
                  </div>
                );
              }
            })()
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-auto">
              <pre className="text-sm">{JSON.stringify(result, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QueryPage;
